import { Currency } from '~/constants/currency';

export const blackbeardWeightUnit = {
  G: 'g',
  KG: 'kg',
  OZ: 'oz',
  LB: 'lb',
} as const;

const inverseWeightUnitMap: Record<BlackbeardWeightUnit, keyof typeof blackbeardWeightUnit> = {
  g: 'G',
  kg: 'KG',
  oz: 'OZ',
  lb: 'LB',
};

type BlackbeardWeightUnitMapType = typeof blackbeardWeightUnit;
export type BlackbeardWeightUnit = BlackbeardWeightUnitMapType[keyof BlackbeardWeightUnitMapType];

function inputIsBlackbeardWeightUnitType(input: string): input is BlackbeardWeightUnit {
  return !!inverseWeightUnitMap[input as unknown as BlackbeardWeightUnit];
}

// TODO: Confirm this works in code and fall back to a reasonable default
export function stringToValidWeightUnit(input: string): BlackbeardWeightUnit {
  if (inputIsBlackbeardWeightUnitType(input)) {
    return input;
  }

  throw new Error(`Invalid weight unit ${input}`);
}

export const blackbeardDistanceUnit = {
  CM: 'cm',
  IN: 'in',
  MM: 'mm',
} as const;

const inverseDistanceUnitMap: Record<BlackbeardDistanceUnit, keyof typeof blackbeardDistanceUnit> =
  {
    cm: 'CM',
    in: 'IN',
    mm: 'MM',
  };

type BlackbeardDistanceUnitMapType = typeof blackbeardDistanceUnit;
export type BlackbeardDistanceUnit =
  BlackbeardDistanceUnitMapType[keyof BlackbeardDistanceUnitMapType];

function inputIsBlackbearDistanceUnitType(input: string): input is BlackbeardDistanceUnit {
  return !!inverseDistanceUnitMap[input as unknown as BlackbeardDistanceUnit];
}

// TODO: Confirm this works in code and fall back to a reasonable default
export function stringToValidDistanceUnit(input: string): BlackbeardDistanceUnit {
  if (inputIsBlackbearDistanceUnitType(input)) {
    return input;
  } else {
    throw new Error(`Invalid distance unit ${input}`);
  }
}

type BaseParcelTemplate = {
  name: string;
  height: number;
  length: number;
  width: number;
  distanceUnit: BlackbeardDistanceUnit;
};

export interface BlackbeardCarrierParcelTemplate extends BaseParcelTemplate {
  token: string;
  provider: string;
  isVariableDimensions: boolean;
}

export type BlackbeardParcelTemplate = BaseParcelTemplate & {
  objectId?: string;
  carrierTemplate: Maybe<BlackbeardCarrierParcelTemplate>;
  isDefault: boolean;
  weightUnit?: BlackbeardWeightUnit;
  weight?: number;
};

export type BlackbeardParcel = {
  length: string;
  width: string;
  height: string;
  distanceUnit: string;
  weight: string;
  weightUnit: string;
  userParcelTemplate: Maybe<BlackbeardParcelTemplate>;
  carrierParcelTemplate: Maybe<BlackbeardCarrierParcelTemplate>;
  useImportedOrderWeight?: boolean;
  saveAsTemplate?: boolean;
};

export class SelectedParcelType {
  private oneOffBoxOrTubeSelected: boolean;
  private oneOffPolymailerSelected: boolean;
  private selectedUserParcelTemplate?: BlackbeardParcelTemplate;
  private selectedCarrierParcelTemplate?: BlackbeardCarrierParcelTemplate;

  public static unselected(): SelectedParcelType {
    return new SelectedParcelType(false, false);
  }

  public static selectCustomBoxOrTube(): SelectedParcelType {
    return new SelectedParcelType(true, false);
  }

  public static selectCustomPolymailer(): SelectedParcelType {
    return new SelectedParcelType(false, true);
  }

  public static selectUserParcelTemplate(template: BlackbeardParcelTemplate): SelectedParcelType {
    return new SelectedParcelType(false, false, template);
  }

  public static selectCarrierParcelTemplate(
    template: BlackbeardCarrierParcelTemplate,
  ): SelectedParcelType {
    return new SelectedParcelType(false, false, undefined, template);
  }

  public selectFromTemplate(
    userTemplate?: BlackbeardParcelTemplate,
    carrierTemplate?: BlackbeardCarrierParcelTemplate,
  ): SelectedParcelType {
    if (!!userTemplate || !!carrierTemplate) {
      return new SelectedParcelType(false, false, userTemplate, carrierTemplate);
    }

    return this;
  }

  public isUnselected(): boolean {
    return !this.isCustomSelected() && !this.isTemplateSelected();
  }

  public isCustomSelected(): boolean {
    return this.isCustomBoxOrTubeSelected() || this.isCustomPolymailerSelected();
  }

  public isTemplateSelected(): boolean {
    return this.isUserParcelTemplateSelected() || this.isCarrierParcelTemplateSelected();
  }

  public isCustomBoxOrTubeSelected(): boolean {
    return this.oneOffBoxOrTubeSelected;
  }

  public isCustomPolymailerSelected(): boolean {
    return this.oneOffPolymailerSelected;
  }

  public isUserParcelTemplateSelected(): boolean {
    return this.selectedUserParcelTemplate !== undefined;
  }

  public isCarrierParcelTemplateSelected(): boolean {
    return this.selectedCarrierParcelTemplate !== undefined;
  }

  public getSelectedUserParcelTemplate(): BlackbeardParcelTemplate | undefined {
    return this.selectedUserParcelTemplate;
  }

  public getSelectedCarrierParcelTemplate(): BlackbeardCarrierParcelTemplate | undefined {
    return this.selectedCarrierParcelTemplate;
  }

  public getSelectedParcelTemplate():
    | BlackbeardParcelTemplate
    | BlackbeardCarrierParcelTemplate
    | undefined {
    return this.isUserParcelTemplateSelected()
      ? this.getSelectedUserParcelTemplate()
      : this.getSelectedCarrierParcelTemplate();
  }

  public isEqual(other: SelectedParcelType): boolean {
    if (this.isUnselected()) {
      return other.isUnselected();
    }

    if (this.isCustomBoxOrTubeSelected()) {
      return other.isCustomBoxOrTubeSelected();
    }

    if (this.isCustomPolymailerSelected()) {
      return other.isCustomPolymailerSelected();
    }

    return (
      JSON.stringify(this.getSelectedParcelTemplate()) ===
      JSON.stringify(other.getSelectedParcelTemplate())
    );
  }

  private constructor(
    oneOffBoxOrTubeSelected: boolean,
    oneOffPolymailerSelected: boolean,
    selectedUserParcelTemplate?: BlackbeardParcelTemplate,
    selectedCarrierParcelTemplate?: BlackbeardCarrierParcelTemplate,
  ) {
    this.oneOffBoxOrTubeSelected = oneOffBoxOrTubeSelected;
    this.oneOffPolymailerSelected = oneOffPolymailerSelected;
    this.selectedUserParcelTemplate = selectedUserParcelTemplate;
    this.selectedCarrierParcelTemplate = selectedCarrierParcelTemplate;
  }
}

export type BlackbeardUserTemplates = readonly BlackbeardParcelTemplate[];
export type BlackbeardCarrierTemplates = Readonly<{
  [key: string]: BlackbeardCarrierParcelTemplate[];
}>;

export interface ItemWeightInfo {
  weight: number;
  unit: BlackbeardWeightUnit;
  disableUseWeightOfItems: boolean;
  hasMultipleSources: boolean;
}

export interface DefaultUnits {
  distance: BlackbeardDistanceUnit;
  weight: BlackbeardWeightUnit;
}

export interface TemplatesData {
  userDefined: BlackbeardUserTemplates;
  carrierProvided: BlackbeardCarrierTemplates;
  defaultUnits: DefaultUnits;
  candidateUserTemplate: BlackbeardParcelTemplate | undefined;
  candidateCarrierTemplate: BlackbeardCarrierParcelTemplate | undefined;
}

export interface BlackbeardParcelInput extends BlackbeardParcel {
  userParcelTemplate: BlackbeardParcelTemplate | null;
  carrierParcelTemplate: BlackbeardCarrierParcelTemplate | null;
  useImportedOrderWeight: boolean;
  saveAsTemplate: boolean;
  templateName: string;
  setAsDefault: boolean;
}

export interface MultiSelectBlackbeardParcelInput extends BlackbeardParcel {
  calculatedLength: string;
  multipleLength: boolean;
  calculatedWidth: string;
  multipleWidth: boolean;
  calculatedHeight: string;
  multipleHeight: boolean;
  calculatedDistanceUnit: string;
  multipleDistanceUnit: boolean;
  calculatedWeight: string;
  multipleWeight: boolean;
  calculatedWeightUnit: string;
  multipleWeightUnit: boolean;
  calculatedUseImportedOrderWeight: boolean;
  multipleUseImportedOrderWeight: boolean;
  userParcelTemplate: BlackbeardParcelTemplate | null;
  carrierParcelTemplate: BlackbeardCarrierParcelTemplate | null;
  useImportedOrderWeight: boolean;
  saveAsTemplate: boolean;
  templateName: string;
  setAsDefault: boolean;
}

export type TemplateSelectionType = 'userDefined' | 'carrierProvided' | 'combined' | null;

export interface MinimalLineItem {
  objectId: string;
  quantity: number;
  title: string;
  sku: string;
  totalAmount: string;
  currency: Currency;
  totalWeight: string;
  weightUnit?: BlackbeardWeightUnit | null;
}

export interface MinimumOrder {
  subtotalPrice?: string | null;
  totalPrice?: string | null;
  currency?: string | null;
  lineItems: MinimalLineItem[];
}

export interface BlackbeardLineItemLite {
  objectId: string;
  quantity: number;
}
export interface BlackbeardInsuranceExtra {
  amount: string;
  currency: Currency;
  useImportedAmount: boolean;
}

export const blackbeardSignatureConfirmation = {
  STANDARD: 'STANDARD',
  ADULT: 'ADULT',
  CERTIFIED: 'CERTIFIED',
  INDIRECT: 'INDIRECT',
  CARRIER_CONFIRMATION: 'CARRIER_CONFIRMATION',
} as const;

export type BlackbeardSignatureConfirmation =
  (typeof blackbeardSignatureConfirmation)[keyof typeof blackbeardSignatureConfirmation];

export interface BlackbeardSignatureConfirmationExtra {
  hasSignatureConfirmation: boolean;
  signatureConfirmationType: Maybe<BlackbeardSignatureConfirmation>;
}

type BlackbeardRecipientType = 'LICENSEE' | 'CONSUMER';

interface BlackbeardAlcoholExtra {
  containsAlcohol: boolean;
  recipientType: Maybe<BlackbeardRecipientType>;
}

export interface BlackbeardDryIceExtra {
  containsDryIce: boolean;
  weight: string;
  weightUnitId: number;
}

export interface BlackbeardShipmentExtras {
  signatureConfirmation: Maybe<BlackbeardSignatureConfirmationExtra>;
  isResidential?: boolean;
  isReturnRequested: boolean;
  insurance: Maybe<BlackbeardInsuranceExtra>;
  isBypassAddressValidation: boolean;
  alcohol: Maybe<BlackbeardAlcoholExtra>;
  isBypassCustoms: boolean;
  dryIce: Maybe<BlackbeardDryIceExtra>;
  packingSlipText: Maybe<string>;
}

export const DEFAULT_INSURANCE_MODAL_AMOUNT = 125;

// transit time insights types
export interface TransitInsightStats {
  status: 'early' | 'late' | 'on time';
  days: number;
}
export enum TransitCarrier {
  USPS = 'USPS',
  UPS = 'UPS',
  FedEx = 'FedEx',
}

export const USPS = {
  GroundAdvantage: 'usps_ground_advantage',
  PriorityMail: 'usps_priority_mail',
  PriorityMailExpress: 'usps_priority_mail_express',
};

const UPS = {
  Ground: 'ups_ground',
  GroundSaver: 'ups_ground_saver',
};

const FedEx = {
  HomeDelivery: 'fedex_home_delivery',
  TwoDay: 'fedex_2_day',
  GroundEconomy: 'fedex_ground_economy',
};

interface ServiceLevelRange {
  min: number;
  max: number;
  type: 'business' | 'calendar';
}

export const carrierTransitTimeRange: Record<TransitCarrier, Record<string, ServiceLevelRange>> = {
  [TransitCarrier.USPS]: {
    [USPS.GroundAdvantage]: { min: 2, max: 5, type: 'business' },
    [USPS.PriorityMail]: { min: 1, max: 3, type: 'business' },
    [USPS.PriorityMailExpress]: { min: 2, max: 2, type: 'calendar' },
  },
  [TransitCarrier.UPS]: {
    [UPS.Ground]: { min: 1, max: 5, type: 'business' },
    [UPS.GroundSaver]: { min: 2, max: 7, type: 'business' },
  },
  [TransitCarrier.FedEx]: {
    [FedEx.HomeDelivery]: { min: 1, max: 5, type: 'business' },
    [FedEx.TwoDay]: { min: 2, max: 2, type: 'business' },
    [FedEx.GroundEconomy]: { min: 2, max: 7, type: 'business' },
  },
};
